/**
 * ============================================================================================================
 *  THE MONETIZATION CATALOGUE: the ONE place that says what the game sells and what the opt-in rewarded ads give.
 * ============================================================================================================
 *
 * The server is the single source of truth. Clients learn the products from `store.catalog` and the ad placements
 * from `ads.status`; nothing here is duplicated in a client, in the Android app or in the game. Change a number here,
 * restart the server, and every client follows. The numbers come from the economy analysis (docs/economy.md §3,
 * tools/economy/model.mts --json): keep the two in step.
 *
 * WHAT LIVES WHERE
 *  - Product ids and what a purchase credits (gold / diamonds / items): HERE.
 *  - Prices: the store's (Google Play Console: Monetize > Products > In-app products) are the ones a player pays; the
 *    client shows the store's localized price when the store reports one. `listPrices` here are the owner's price
 *    list (docs/economy.md "Рішення власника" and §4: USD, the default of Play Console, and the manual UAH price):
 *    what the shop shows where no store reports a price (the skeleton of the shop on the web page and in an app build
 *    without payments: the offers are shown, nothing is sold) and what Play Console is to be set to. Keep the two
 *    in step. Every active product id below must exist in Play Console as an active in-app product.
 *  - Ad unit ids and the AdMob app id: the Android build configuration (docs/google-play.md), not here.
 *
 * RULES (checked by tests/backend/monetization-catalog.test.ts and at server start: catalogueProblems):
 *  - productId: Google Play's product id syntax (starts with a lower-case letter or digit; lower-case letters, digits,
 *    '_' and '.'; at most 139 characters), unique. The ids name a price TIER, not the contents: change the amounts
 *    freely, never the id of a product that was sold (a Play product id can never be reused; refunds of old purchases
 *    look it up): retire a product with active: false instead of deleting it.
 *  - gold / diam / items: non-negative integers; a product credits something. items: stimulators (STIM_DEF ids).
 *  - limitPerAccount: null (any number of purchases) or a positive integer (the starter pack: 1).
 *  - shop: where the in-game shop shows it: tab 'diamonds' / 'gold' (the six buttons of the "Buy" block of the
 *    exchange screen, slot 1..6: 1..3 the left column top to bottom, 4..6 the right column) or 'offer' (the offer
 *    button above them); null = not shown in the game. One active product per tab and slot.
 *  - offerWindows: when a limited offer is shown (none = always): each window opens the first time the server sees the
 *    player meet its condition (store.catalog) and stays open for `hours`.
 *  - listPrices: ISO 4217 code -> price in that currency (at most two decimals); USD and UAH always.
 *  - bonusPct: the pack's bonus over the base rate as the shop's badge shows it ("+20 %"), an integer 0..1000; 0 = no
 *    badge. For a bundle (the starter pack) it is its whole value over the base rate (docs/economy.md §4), not shown
 *    as a badge.
 *
 * All products are consumables (Play "one-time products" consumed after the credit), so a pack can be bought again.
 * There are no subscriptions and no non-consumables.
 *
 * REWARDED ADS: STRICT PRODUCT RULE of the owner. Ads are shown ONLY when the player chooses to watch one for a
 * visible bonus (an opt-in button with the exact reward on the screen where the bonus applies). No interstitials, no
 * banners, no ads shown automatically, nothing that blocks progress: the game is fully playable without ever watching
 * an ad. The rewards are granted by the server after the ad provider's server-side verification, at most `dailyCap`
 * times a day per player (the player's calendar day), AD_DAILY_CAP_TOTAL in all.
 */

/** The currencies every product's price list has (the shop shows UAH in Ukrainian, USD otherwise). */
export const LIST_PRICE_CURRENCIES: readonly string[] = ['USD', 'UAH'];

/** Google Play's product id syntax. */
export const PLAY_PRODUCT_ID_PATTERN = /^[a-z0-9][a-z0-9_.]{0,138}$/;

export type StoreProductKind = 'currency_pack' | 'bundle';
export type StoreShopTab = 'diamonds' | 'gold' | 'offer';

/** A condition that opens a limited offer's window (see offerWindows). */
export type OfferTrigger = { kind: 'first_win' } | { kind: 'level'; level: number };

export interface StoreProductDef {
  /** Google Play product id (Play Console, In-app products). Never change it once the product was sold. */
  productId: string;
  kind: StoreProductKind;
  /** Gold / diamonds credited per purchase. */
  gold: number;
  diam: number;
  /** Stimulators credited per purchase (STIM_DEF id -> count). */
  items: Readonly<Record<string, number>>;
  /** Purchases per account (null = unlimited). */
  limitPerAccount: number | null;
  /** The in-game shop's place for it (see the header), or null. */
  shop: { tab: StoreShopTab; slot: number } | null;
  /** A badge of the shop button: 'popular' / 'best_value' (docs/economy.md §3.2), or null. */
  label: 'popular' | 'best_value' | null;
  /** Limited-time windows of the offer (see the header); empty = always offered. */
  offerWindows: readonly { trigger: OfferTrigger; hours: number }[];
  /** The owner's price list (see the header): ISO 4217 code -> price; USD and UAH always. */
  listPrices: Readonly<Record<string, number>>;
  /** The bonus over the base rate in percent (the shop's badge; see the header). */
  bonusPct: number;
  /** false = no longer sold (kept so that refunds of old purchases still find it). */
  active: boolean;
}

/** The price tiers of the owner's price list (docs/economy.md): USD (Play Console's default) and the manual UAH. */
const TIER_PRICES = {
  t099: { USD: 0.99, UAH: 39.99 },
  t199: { USD: 1.99, UAH: 79.99 },
  t499: { USD: 4.99, UAH: 199.99 },
  t999: { USD: 9.99, UAH: 399.99 },
  t1999: { USD: 19.99, UAH: 799.99 },
  t4999: { USD: 49.99, UAH: 1999.99 },
  t9999: { USD: 99.99, UAH: 3999.99 },
} as const;

const pack = (productId: string, currency: 'gold' | 'diam', amount: number, slot: number, price: Record<string, number>, bonusPct: number, label: StoreProductDef['label'] = null): StoreProductDef => ({
  productId,
  kind: 'currency_pack',
  gold: currency === 'gold' ? amount : 0,
  diam: currency === 'diam' ? amount : 0,
  items: {},
  limitPerAccount: null,
  shop: { tab: currency === 'gold' ? 'gold' : 'diamonds', slot },
  label,
  offerWindows: [],
  listPrices: price,
  bonusPct,
  active: true,
});

/**
 * What the game sells: the owner's decision of 28.09.2026 (docs/economy.md "Рішення власника" and §4: every pack 25
 * times smaller than the first proposal, 2 diamonds for $0.99), with the owner's USD / UAH prices and the bonus
 * badges. 1 diamond = 1100 gold at the in-game exchange.
 */
export const STORE_PRODUCTS: readonly StoreProductDef[] = [
  pack('diamonds_tier1', 'diam', 2, 1, TIER_PRICES.t099, 0),
  pack('diamonds_tier2', 'diam', 11, 2, TIER_PRICES.t499, 10),
  pack('diamonds_tier3', 'diam', 24, 3, TIER_PRICES.t999, 20, 'popular'),
  pack('diamonds_tier4', 'diam', 52, 4, TIER_PRICES.t1999, 30),
  pack('diamonds_tier5', 'diam', 140, 5, TIER_PRICES.t4999, 40),
  pack('diamonds_tier6', 'diam', 300, 6, TIER_PRICES.t9999, 50, 'best_value'),
  pack('gold_tier1', 'gold', 2420, 1, TIER_PRICES.t099, 10),
  pack('gold_tier2', 'gold', 13_200, 2, TIER_PRICES.t499, 20),
  pack('gold_tier3', 'gold', 28_600, 3, TIER_PRICES.t999, 30),
  pack('gold_tier4', 'gold', 61_600, 4, TIER_PRICES.t1999, 40),
  {
    // Once per account: 72 h after the first won battle, and again 48 h from level 10 (§3.4).
    productId: 'starter_pack',
    kind: 'bundle',
    gold: 2000,
    diam: 12,
    items: { stimpack: 2 },
    limitPerAccount: 1,
    shop: { tab: 'offer', slot: 1 },
    label: null,
    offerWindows: [
      { trigger: { kind: 'first_win' }, hours: 72 },
      { trigger: { kind: 'level', level: 10 }, hours: 48 },
    ],
    listPrices: TIER_PRICES.t199,
    bonusPct: 252,
    active: true,
  },
];

/**
 * The "Diamond magnate" award of the original (player.diam_magnet, set by its payment server for a purchase of at
 * least 100 diamonds): set when one purchase credits at least this many diamonds (the first-purchase multiplier
 * included).
 */
export const DIAM_MAGNET_MIN_DIAMONDS = 100;

/**
 * First purchase x2 (§3.4): the FIRST purchase of a product whose id starts with `appliesToPrefix` is credited
 * `multiplier` times, once per account (the diamonds; the starter pack does not count and does not use it up).
 */
export const FIRST_PURCHASE = { appliesToPrefix: 'diamonds_tier', multiplier: 2 } as const;

/** The opt-in rewarded ad placements of the game (where the button appears: docs/google-play.md). */
export type AdPlacementId = 'ad_battle_double' | 'ad_grave_extra' | 'ad_darts_free' | 'ad_daily_gift_x2' | 'ad_rest';

export const AD_PLACEMENT_IDS: readonly AdPlacementId[] = ['ad_battle_double', 'ad_grave_extra', 'ad_darts_free', 'ad_daily_gift_x2', 'ad_rest'];

export interface AdPlacementDef {
  /** false = the button is never offered (the server refuses ads.start). */
  enabled: boolean;
  /** Rewards per player per calendar day (the player's time zone). */
  dailyCap: number;
}

/**
 * The placements (docs/economy.md §3.5 and the owner's decision: ad_diamond dropped, 12 views a day in all), each an
 * opt-in button with its exact reward:
 *  - ad_battle_double: victory screen of a career / missions / survival battle (not bosses, not assaults): the gold of
 *    that victory once more (x2), the bonus at most BATTLE_DOUBLE_MAX_GOLD. Once per battle.
 *  - ad_grave_extra: the find of a searched grave: one more roll of the same grave by the game's grave table, without
 *    diamonds (a diamond result becomes the gold result) and without tiredness. Once per grave.
 *  - ad_darts_free: darts screen: a free throw at the bet DARTS_FREE_BET (its prize table).
 *  - ad_daily_gift_x2: daily present screen: the present twice (the original's x2 present).
 *  - ad_rest: when tiredness is at least AD_REST_MIN_TIRED: tiredness - AD_REST_AMOUNT (a super stimpack's worth).
 */
export const AD_PLACEMENTS: Readonly<Record<AdPlacementId, AdPlacementDef>> = {
  ad_battle_double: { enabled: true, dailyCap: 4 },
  ad_grave_extra: { enabled: true, dailyCap: 3 },
  ad_darts_free: { enabled: true, dailyCap: 2 },
  ad_daily_gift_x2: { enabled: true, dailyCap: 1 },
  ad_rest: { enabled: true, dailyCap: 2 },
};

/** All placements together, per player and day. */
export const AD_DAILY_CAP_TOTAL = 12;
/** ad_battle_double: the modes it applies to and the largest bonus. */
export const BATTLE_DOUBLE_MODES: readonly string[] = ['career', 'missions', 'survival'];
export const BATTLE_DOUBLE_MAX_GOLD = 800;
/** ad_darts_free: the bet a free throw plays (one of the darts bets: gold 500 / 3000 / 10000, diam 5 / 25 / 50). */
export const DARTS_FREE_BET: { currency: 'gold' | 'diam'; amount: number } = { currency: 'gold', amount: 3000 };
/** ad_rest: offered from this tiredness on, and how much it takes away. */
export const AD_REST_MIN_TIRED = 50;
export const AD_REST_AMOUNT = 30;

/** The problems of a catalogue (empty = valid): the rules of the header. */
export function catalogueProblems(products: readonly StoreProductDef[] = STORE_PRODUCTS, placements: Readonly<Record<string, AdPlacementDef>> = AD_PLACEMENTS, stimIds?: readonly string[]): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  const places = new Map<string, string[]>();
  const count = (v: number) => Number.isSafeInteger(v) && v >= 0 && v <= 100_000_000;
  for (const p of products) {
    if (!PLAY_PRODUCT_ID_PATTERN.test(p.productId)) out.push(`${p.productId}: not a Google Play product id`);
    if (ids.has(p.productId)) out.push(`${p.productId}: duplicate product id`);
    ids.add(p.productId);
    if (!count(p.gold) || !count(p.diam)) out.push(`${p.productId}: gold and diam must be integers 0..100000000`);
    const items = Object.entries(p.items);
    for (const [id, n] of items) {
      if (!Number.isSafeInteger(n) || n < 1 || n > 1000) out.push(`${p.productId}: item ${id} must be a count 1..1000`);
      if (stimIds && !stimIds.includes(id)) out.push(`${p.productId}: item ${id} is not a stimulator (STIM_DEF)`);
    }
    if (p.gold + p.diam <= 0 && items.length === 0) out.push(`${p.productId}: credits nothing`);
    if (p.limitPerAccount !== null && (!Number.isSafeInteger(p.limitPerAccount) || p.limitPerAccount < 1)) out.push(`${p.productId}: limitPerAccount must be null or a positive integer`);
    if (p.kind === 'bundle' && p.limitPerAccount === null) out.push(`${p.productId}: a bundle needs a limitPerAccount`);
    if (p.shop && (!Number.isInteger(p.shop.slot) || p.shop.slot < 1 || p.shop.slot > 6)) out.push(`${p.productId}: shop slot must be 1..6`);
    if (p.shop?.tab === 'gold' && p.diam > 0) out.push(`${p.productId}: a gold tab product credits no diamonds`);
    if (p.shop?.tab === 'diamonds' && p.gold > 0) out.push(`${p.productId}: a diamonds tab product credits no gold`);
    for (const w of p.offerWindows) if (!Number.isInteger(w.hours) || w.hours < 1 || w.hours > 24 * 365) out.push(`${p.productId}: offer window hours must be 1..8760`);
    for (const code of LIST_PRICE_CURRENCIES) if (!(code in p.listPrices)) out.push(`${p.productId}: listPrices needs ${code}`);
    for (const [code, v] of Object.entries(p.listPrices)) {
      if (!/^[A-Z]{3}$/.test(code)) out.push(`${p.productId}: listPrices ${code} is not an ISO 4217 code`);
      if (!(typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 100_000 && Math.abs(Math.round(v * 100) - v * 100) < 1e-6)) out.push(`${p.productId}: listPrices ${code} must be a price with at most two decimals`);
    }
    if (!Number.isInteger(p.bonusPct) || p.bonusPct < 0 || p.bonusPct > 1000) out.push(`${p.productId}: bonusPct must be an integer 0..1000`);
    if (p.active && p.shop) {
      const key = `${p.shop.tab}:${p.shop.slot}`;
      places.set(key, [...(places.get(key) ?? []), p.productId]);
    }
  }
  for (const [key, list] of places) if (list.length > 1) out.push(`shop ${key}: more than one active product (${list.join(', ')})`);
  if (!Number.isInteger(FIRST_PURCHASE.multiplier) || FIRST_PURCHASE.multiplier < 1) out.push('FIRST_PURCHASE.multiplier must be a positive integer');
  let total = 0;
  for (const [id, pl] of Object.entries(placements)) {
    if (!(AD_PLACEMENT_IDS as readonly string[]).includes(id)) out.push(`${id}: unknown ad placement`);
    if (!Number.isInteger(pl.dailyCap) || pl.dailyCap < 0 || pl.dailyCap > 100) out.push(`${id}: dailyCap must be an integer 0..100`);
    if (pl.enabled) total += pl.dailyCap;
  }
  if (total > AD_DAILY_CAP_TOTAL) out.push(`ad placements: daily caps add up to ${total}, above AD_DAILY_CAP_TOTAL ${AD_DAILY_CAP_TOTAL}`);
  const bets: Record<string, number[]> = { gold: [500, 3000, 10000], diam: [5, 25, 50] };
  if (!bets[DARTS_FREE_BET.currency]?.includes(DARTS_FREE_BET.amount)) out.push('DARTS_FREE_BET: not one of the darts bets');
  for (const [name, v] of Object.entries({ BATTLE_DOUBLE_MAX_GOLD, AD_REST_MIN_TIRED, AD_REST_AMOUNT, DIAM_MAGNET_MIN_DIAMONDS })) if (!Number.isInteger(v) || v < 1) out.push(`${name} must be a positive integer`);
  return out;
}

/** The catalogue entry of a product id (active or not), or null. */
export function storeProduct(productId: string, products: readonly StoreProductDef[] = STORE_PRODUCTS): StoreProductDef | null {
  return products.find((p) => p.productId === productId) ?? null;
}

/** Whether the first-purchase multiplier applies to a product (FIRST_PURCHASE). */
export function firstPurchaseApplies(productId: string): boolean {
  return productId.startsWith(FIRST_PURCHASE.appliesToPrefix);
}
